import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiUpload } from 'react-icons/fi';
import API from '../../api/axios';
import { getImageUrl } from '../../utils/helpers';
import toast from 'react-hot-toast';

const defaultForm = {
  title: '', subtitle: '', link: '', buttonText: 'Shop Now',
  position: 'hero', isActive: true, order: 0,
  backgroundColor: '#1e3a5f', textColor: '#ffffff', image: '',
};

const AdminBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchBanners = () => {
    setLoading(true);
    API.get('/banners/all').then(({ data }) => setBanners(data)).catch(() =>
      API.get('/banners').then(({ data }) => setBanners(data)).catch(() => {})
    ).finally(() => setLoading(false));
  };

  useEffect(() => { fetchBanners(); }, []);

  const openEdit = (b) => {
    setEditId(b._id);
    setForm({ title: b.title, subtitle: b.subtitle || '', link: b.link || '', buttonText: b.buttonText || 'Shop Now', position: b.position || 'hero', isActive: b.isActive, order: b.order || 0, backgroundColor: b.backgroundColor || '#1e3a5f', textColor: b.textColor || '#ffffff', image: b.image || '' });
    setShowForm(true);
  };

  const openNew = () => { setEditId(null); setForm(defaultForm); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditId(null); };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error('Title is required');
    setSaving(true);
    try {
      if (editId) await API.put(`/banners/${editId}`, form);
      else await API.post('/banners', form);
      toast.success(editId ? 'Banner updated!' : 'Banner created!');
      fetchBanners();
      closeForm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving banner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await API.delete(`/banners/${id}`);
      toast.success('Banner deleted');
      fetchBanners();
    } catch {
      toast.error('Error deleting banner');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Banners & Images</h1>
        <button onClick={openNew} className="btn-primary flex items-center gap-2">
          <FiPlus size={16} /> Add Banner
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Banner list */}
        <div className="lg:col-span-2 space-y-3">
          {loading ? (
            [...Array(3)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />)
          ) : banners.length === 0 ? (
            <div className="card p-10 text-center text-gray-400">
              <p>No banners yet. Create your first banner!</p>
            </div>
          ) : banners.map((banner) => (
            <div
              key={banner._id}
              className="card p-4 flex items-center gap-4"
            >
              {/* Preview */}
              <div
                className="w-32 h-20 rounded-xl flex-shrink-0 overflow-hidden relative flex items-center justify-center"
                style={{ backgroundColor: banner.backgroundColor || '#1e3a5f' }}
              >
                {banner.image && (
                  <img src={getImageUrl(banner.image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                )}
                <p className="relative text-xs font-bold px-2 text-center" style={{ color: banner.textColor || '#fff' }}>
                  {banner.title}
                </p>
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 truncate">{banner.title}</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  <span className="badge bg-blue-100 text-blue-700 capitalize">{banner.position}</span>
                  <span className={`badge ${banner.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {banner.isActive ? 'Active' : 'Hidden'}
                  </span>
                  <span className="text-xs text-gray-400">Order: {banner.order}</span>
                </div>
              </div>

              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => openEdit(banner)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg">
                  <FiEdit2 size={15} />
                </button>
                <button onClick={() => handleDelete(banner._id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg">
                  <FiTrash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        {showForm && (
          <div className="card p-6 h-fit">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-bold text-gray-800">{editId ? 'Edit Banner' : 'New Banner'}</h2>
              <button onClick={closeForm} className="p-1 hover:bg-gray-100 rounded-lg"><FiX size={16} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Title *</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="input-field" placeholder="Banner title" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Subtitle</label>
                <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="input-field" placeholder="Subtitle text" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Link URL</label>
                <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="input-field" placeholder="/shop or /shop?category=..." />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Button Text</label>
                <input value={form.buttonText} onChange={(e) => setForm({ ...form, buttonText: e.target.value })} className="input-field" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Position</label>
                <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="input-field">
                  <option value="hero">Hero (Top)</option>
                  <option value="middle">Middle</option>
                  <option value="bottom">Bottom</option>
                  <option value="sidebar">Sidebar</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">BG Color</label>
                  <div className="flex gap-2">
                    <input type="color" value={form.backgroundColor} onChange={(e) => setForm({ ...form, backgroundColor: e.target.value })} className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                    <input value={form.backgroundColor} onChange={(e) => setForm({ ...form, backgroundColor: e.target.value })} className="input-field text-xs" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Text Color</label>
                  <div className="flex gap-2">
                    <input type="color" value={form.textColor} onChange={(e) => setForm({ ...form, textColor: e.target.value })} className="w-10 h-10 rounded border border-gray-300 cursor-pointer" />
                    <input value={form.textColor} onChange={(e) => setForm({ ...form, textColor: e.target.value })} className="input-field text-xs" />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Display Order</label>
                <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="input-field" min="0" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Background Image</label>
                <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-xl p-3 cursor-pointer hover:border-primary-400">
                  <FiUpload size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-500">{uploading ? 'Uploading...' : 'Upload image'}</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="hidden" />
                </label>
                {form.image && (
                  <div className="relative mt-2 w-full h-20 rounded-xl overflow-hidden" style={{ backgroundColor: form.backgroundColor }}>
                    <img src={getImageUrl(form.image)} alt="" className="w-full h-full object-cover opacity-60" />
                    <button type="button" onClick={() => setForm({ ...form, image: '' })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5"><FiX size={10} /></button>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4 accent-primary-500" />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <div className="flex gap-2">
                <button type="submit" disabled={saving} className="btn-primary flex items-center gap-1 flex-1">
                  <FiCheck size={14} /> {saving ? 'Saving...' : 'Save Banner'}
                </button>
                <button type="button" onClick={closeForm} className="btn-secondary px-4">Cancel</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBanners;
